import React, { useEffect, useState } from 'react';
import { Box, Checkbox, IconButton, TextField, Typography } from '@mui/material';
import { DragIndicator, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { SonosSpeaker } from '../../services/sonosApi';
import {
  sonosCardOuterSx,
  SonosCardIcon,
  SonosCardPlaybackBody,
  SonosCardAiBadge,
} from './sonosCardLayout';

interface SonosSpeakerCardSettingsProps {
  speaker: SonosSpeaker;
  isSelected: boolean;
  editMode: boolean;
  isRoomEditing?: boolean;
  isEditingName?: boolean;
  dragHandleProps?: Record<string, unknown>;
  onSelect?: (id: number) => void;
  onClick?: (speaker: SonosSpeaker) => void;
  onTransport?: (speakerId: number, action: 'play' | 'pause' | 'previous' | 'next') => void;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
}

/**
 * Settings card Sonos.
 * Badges (PIN/IA) vía SonosCardAiBadge (izquierda de la tapa, debajo del tema).
 * En editMode el play se oculta (queda el checkbox rojo); lápiz junto al nombre.
 */
const SonosSpeakerCardSettings: React.FC<SonosSpeakerCardSettingsProps> = ({
  speaker,
  isSelected,
  editMode,
  isRoomEditing = false,
  isEditingName = false,
  dragHandleProps,
  onSelect,
  onClick,
  onTransport,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
}) => {
  const [editingNameValue, setEditingNameValue] = useState(speaker.nombre);

  useEffect(() => {
    if (isEditingName) {
      setEditingNameValue(speaker.nombre);
    }
  }, [isEditingName, speaker.id, speaker.nombre]);

  const handleTransport = (speakerId: number, action: 'play' | 'pause') => {
    onTransport?.(speakerId, action);
  };

  // Misma fila de nombre + lápiz que DeviceCard (Shelly): tipografía flex:1 + lápiz al borde del área de texto.
  const nameSlot = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: 0.15,
      position: 'relative',
      zIndex: isEditingName ? 100 : 'auto',
      minWidth: 0,
    }}>
      {isEditingName ? (
        <>
          <TextField
            value={editingNameValue}
            onChange={(e) => setEditingNameValue(e.target.value)}
            variant="standard"
            autoFocus
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiInput-root': {
                color: 'white',
                fontSize: 14,
                fontWeight: 'bold',
              },
              '& .MuiInput-underline:before': { borderBottomColor: '#666' },
              '& .MuiInput-underline:after': { borderBottomColor: '#2391FF' },
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && onSaveEditName) {
                onSaveEditName(editingNameValue);
              }
            }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSaveEditName?.(editingNameValue);
            }}
            sx={{
              padding: 1,
              marginLeft: 0.5,
              minWidth: 32,
              minHeight: 32,
              zIndex: 101,
              flexShrink: 0,
              backgroundColor: 'rgba(35, 145, 255, 0.1)',
            }}
          >
            <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCancelEditName?.();
            }}
            sx={{
              padding: 1,
              marginLeft: 0.5,
              minWidth: 32,
              minHeight: 32,
              zIndex: 101,
              flexShrink: 0,
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
            }}
          >
            <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
          </IconButton>
        </>
      ) : (
        <>
          <Typography sx={{
            fontSize: 14,
            fontWeight: 'bold',
            color: speaker.online ? '#fff' : '#e57373',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}>
            {speaker.nombre}
          </Typography>
          {editMode && !isRoomEditing && onStartEditName && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onStartEditName();
              }}
              sx={{
                padding: 0.25,
                marginLeft: 0.5,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
              }}
            >
              <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        border: isSelected ? '1px solid #2391FF' : 'none',
        opacity: speaker.online ? 1 : 0.5,
        // Scroll móvil (docs/51): eventos pasan al contenedor; overlay pan-y para tap.
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & .MuiCheckbox-root, & [data-device-icon], & .MuiInput-root, & [data-audio-player-control]': {
          pointerEvents: 'auto',
          touchAction: isRoomEditing ? 'none' : 'manipulation',
        },
      }}
    >
      {!isRoomEditing && !isEditingName && (
        <Box
          onClick={(e) => {
            if (editMode) return;
            const target = e.target as HTMLElement;
            if (
              target.closest('button')
              || target.closest('.MuiIconButton-root')
              || target.closest('.MuiCheckbox-root')
              || target.closest('[data-device-icon]')
              || target.closest('[data-audio-player-control]')
            ) {
              return;
            }
            const scrolled = (window as any).dashboardHasScrolledRef?.current;
            if (!scrolled) onClick?.(speaker);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px',
            right: '140px',
            bottom: 0,
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}
      <SonosCardAiBadge speaker={speaker} />
      <SonosCardIcon speaker={speaker} showBrandChip={editMode} />
      <SonosCardPlaybackBody
        speaker={speaker}
        canControl={speaker.can_control !== false}
        onTransport={handleTransport}
        showControl={!editMode && !isRoomEditing && !isEditingName}
        // En edición: ocultar play pero conservar columna → lápiz alineado con Shelly
        reserveControlSpace={editMode && !isRoomEditing}
        nameSlot={nameSlot}
      />

      {/* sx idéntica a DeviceCard.tsx (líneas ~1396–1415) */}
      {isRoomEditing && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            // Shelly usa +40px; Sonos quedaba demasiado a la derecha → -50px ⇒ -10px
            transform: 'translateX(-10px)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicator
            sx={{
              color: 'white',
              fontSize: '2.25rem',
              pointerEvents: 'none',
            }}
          />
        </Box>
      )}

      {editMode && onSelect && !isRoomEditing && (
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(speaker.id);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            bottom: '-5px',
            right: '-5px',
            color: 'red',
            pointerEvents: 'auto',
            '& .MuiSvgIcon-root': { color: 'red' },
          }}
        />
      )}
    </Box>
  );
};

export default SonosSpeakerCardSettings;
